import React from 'react'
// c:\xampp\htdocs\miners-market-main\pages\AdBlocksPage.js

function AdBlockManagerModal({ isOpen, onClose, block, onSave }) {
    const [formData, setFormData] = React.useState({
        id: block?.id || null,
        title: block?.title || '',
        subtitle: block?.subtitle || '',
        link: block?.link || '',
        position: block?.position || 1,
        is_active: block?.is_active !== undefined ? block.is_active : 1,
        image: null,
        existing_image_path: block?.image || '',
        product_id: block?.product_id || '',
        product_data: typeof block?.product_data === 'string' ? block.product_data : (block?.product_data ? JSON.stringify(block.product_data) : ''),
        custom_title: block?.title || '',
        custom_subtitle: block?.subtitle || ''
    });
    const [productSearch, setProductSearch] = React.useState('');
    const [productResults, setProductResults] = React.useState([]);
    const [searchingProducts, setSearchingProducts] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState(() => {
        if (!block?.product_data) return null;
        try {
            return typeof block.product_data === 'string' ? JSON.parse(block.product_data) : block.product_data;
        } catch (e) {
            return null;
        }
    });
    const [mediaFiles, setMediaFiles] = React.useState([]);
    const [newMediaFiles, setNewMediaFiles] = React.useState([]);
    const [mediaToDelete, setMediaToDelete] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    if (!isOpen) return null;

    // Load existing media when editing
    React.useEffect(() => {
        if (block && block.media) {
            setMediaFiles(block.media || []);
        } else {
            setMediaFiles([]);
        }
        setNewMediaFiles([]);
        setMediaToDelete([]);
    }, [block, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, image: e.target.files[0] }));
    };

    const handleMediaFilesChange = (e) => {
        const files = Array.from(e.target.files);
        setNewMediaFiles(prev => [...prev, ...files]);
    };

    const removeNewMediaFile = (index) => {
        setNewMediaFiles(prev => prev.filter((_, i) => i !== index));
    };

    const markMediaForDeletion = (mediaId) => {
        setMediaToDelete(prev => [...prev, mediaId]);
        setMediaFiles(prev => prev.filter(m => m.id !== mediaId));
    };

    const formatPrice = (price) => {
        if (!price) return 'POR';
        return `R ${parseFloat(price).toLocaleString()}`;
    };

    // Debounced product search so admins can link a real product to the ad block
    React.useEffect(() => {
        if (!productSearch.trim()) {
            setProductResults([]);
            return;
        }
        const t = setTimeout(async () => {
            setSearchingProducts(true);
            try {
                const res = await fetch(`/api/products.php?search=${encodeURIComponent(productSearch)}&limit=20&main_categories=equipment,consumables,components,diesel,rebuilds,rentals,commercial,services`);
                const data = await res.json();
                setProductResults(data.products || []);
            } catch (e) {
                setProductResults([]);
            } finally {
                setSearchingProducts(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [productSearch]);

    // When editing an existing block, refresh the previously selected product
    React.useEffect(() => {
        if (!block) return;
        let snapshot = null;
        if (block.product_data) {
            try {
                snapshot = typeof block.product_data === 'string' ? JSON.parse(block.product_data) : block.product_data;
            } catch (e) {
                snapshot = null;
            }
        }
        const productId = block.product_id || (snapshot && snapshot.id);
        const listingType = snapshot && (snapshot.listingType || snapshot.listing_type);
        const searchName = (snapshot && snapshot.name) || block.title;
        if (!searchName) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/products.php?search=${encodeURIComponent(searchName)}&limit=20&main_categories=equipment,consumables,components,diesel,rebuilds,rentals,commercial,services`);
                if (!res.ok) return;
                const data = await res.json();
                const results = data.products || [];
                const match = (productId ? results.find(item => {
                    if (String(item.id) !== String(productId)) return false;
                    if (listingType && item.listingType) return item.listingType === listingType;
                    if (item.name) return item.name === searchName;
                    return true;
                }) : null)
                    || results.find(item => item.name === searchName)
                    || null;
                if (cancelled || !match) return;
                setSelectedProduct(match);
                setFormData(prev => ({ ...prev, product_id: match.id || productId, product_data: JSON.stringify(match) }));
            } catch (e) {
                // Keep the existing snapshot on failure
            }
        })();
        return () => { cancelled = true; };
    }, [block]);

    const selectProduct = (p) => {
        setSelectedProduct(p);
        setFormData(prev => ({ ...prev, product_id: p.id, product_data: JSON.stringify(p) }));
        setProductResults([]);
        setProductSearch('');
    };

    const clearSelectedProduct = () => {
        setSelectedProduct(null);
        setFormData(prev => ({ ...prev, product_id: '', product_data: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        const submissionData = new FormData();
        submissionData.append('action', formData.id ? 'update' : 'add');
        if (formData.id) submissionData.append('id', formData.id);
        // Use custom title/subtitle if no product is selected
        const title = selectedProduct ? selectedProduct.name : (formData.custom_title || '');
        const subtitle = selectedProduct ? '' : (formData.custom_subtitle || '');
        submissionData.append('title', title);
        submissionData.append('subtitle', subtitle);
        submissionData.append('link', '');
        submissionData.append('position', formData.position);
        submissionData.append('is_active', formData.is_active);
        submissionData.append('existing_image_path', '');
        submissionData.append('product_id', formData.product_id || '');
        submissionData.append('product_data', formData.product_data || '');

        // Append media files
        newMediaFiles.forEach(file => {
            submissionData.append('media_files[]', file);
        });

        // Append media IDs to delete
        if (mediaToDelete.length > 0) {
            submissionData.append('delete_media_ids', mediaToDelete.join(','));
        }

        try {
            const response = await fetch('/api/manage_ad_block.php', {
                method: 'POST',
                credentials: 'include',
                body: submissionData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to save ad block.');
            onSave();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{block ? 'Edit' : 'Add'} Ad Block</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Heading / Title *</label>
                        <input
                            type="text"
                            value={formData.custom_title}
                            onChange={(e) => setFormData(prev => ({ ...prev, custom_title: e.target.value, title: e.target.value }))}
                            placeholder="e.g. Special Offer, New Arrival..."
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                        <input
                            type="text"
                            value={formData.custom_subtitle}
                            onChange={(e) => setFormData(prev => ({ ...prev, custom_subtitle: e.target.value, subtitle: e.target.value }))}
                            placeholder="Optional subtitle text"
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Link a Product <span className="text-gray-400 font-normal">(optional)</span></label>
                        <p className="mt-1 text-xs text-gray-500">Optionally link a product. Its image and price will be shown, and tapping the block opens the product. Leave empty for a simple image/text block.</p>
                        {selectedProduct ? (
                            <div className="mt-2 flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                                <img src={selectedProduct.image_url_1 || selectedProduct.image_url_2 || selectedProduct.image_url_3 || selectedProduct.image_url_4 || 'images/products/Wooden Box.png'} alt={selectedProduct.name} className="h-12 w-12 object-cover rounded" onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{selectedProduct.name}</div>
                                    <div className="text-xs text-gray-500">{selectedProduct.category_name || selectedProduct.listingType || 'Product'} · {formatPrice(selectedProduct.price)}</div>
                                </div>
                                <button type="button" onClick={clearSelectedProduct} className="text-red-600 text-sm font-medium hover:underline">Remove</button>
                            </div>
                        ) : (
                            <div className="mt-2">
                                <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products by name..." className="block w-full border-gray-300 rounded-md shadow-sm" />
                                {searchingProducts && <p className="mt-1 text-xs text-gray-400">Searching...</p>}
                                {productResults.length > 0 && (
                                    <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
                                        {productResults.map(p => (
                                            <button type="button" key={p.id} onClick={() => selectProduct(p)} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3">
                                                <img src={p.image_url_1 || p.image_url_2 || p.image_url_3 || p.image_url_4 || 'images/products/Wooden Box.png'} alt={p.name} className="h-10 w-10 object-cover rounded" onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                                                    <div className="text-xs text-gray-500">{formatPrice(p.price)}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Media Upload Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Media Files (Images & PDFs)</label>
                        <p className="mt-1 text-xs text-gray-500">Upload additional images and PDF documents. These will be displayed in a full-screen viewer when users tap the ad block.</p>

                        {/* Existing media */}
                        {mediaFiles.length > 0 && (
                            <div className="mt-3 grid grid-cols-3 gap-3">
                                {mediaFiles.map(media => (
                                    <div key={media.id} className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                        {media.file_type === 'pdf' ? (
                                            <div className="h-24 flex items-center justify-center bg-red-50">
                                                <div className="text-center">
                                                    <i className="fas fa-file-pdf text-red-500 text-2xl"></i>
                                                    <p className="text-xs text-gray-600 mt-1 truncate px-2">{media.file_name}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <img src={`/${media.file_path}`} alt={media.file_name} className="w-full h-24 object-cover" onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }} />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => markMediaForDeletion(media.id)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* New media previews */}
                        {newMediaFiles.length > 0 && (
                            <div className="mt-3 grid grid-cols-3 gap-3">
                                {newMediaFiles.map((file, index) => (
                                    <div key={index} className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                        {file.type === 'application/pdf' ? (
                                            <div className="h-24 flex items-center justify-center bg-red-50">
                                                <div className="text-center">
                                                    <i className="fas fa-file-pdf text-red-500 text-2xl"></i>
                                                    <p className="text-xs text-gray-600 mt-1 truncate px-2">{file.name}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-24 object-cover" />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeNewMediaFile(index)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-3">
                            <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                                <i className="fas fa-cloud-upload-alt mr-2"></i>
                                <span className="text-sm font-medium">Add Images & PDFs</span>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*,.pdf"
                                    onChange={handleMediaFilesChange}
                                    className="hidden"
                                />
                            </label>
                            <p className="mt-1 text-xs text-gray-400">Supports: JPG, PNG, GIF, SVG, WebP, PDF</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Block Position (1-4)*</label>
                        <select name="position" value={formData.position} onChange={handleChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                            <option value="1">Block 1</option>
                            <option value="2">Block 2</option>
                            <option value="3">Block 3</option>
                            <option value="4">Block 4</option>
                        </select>
                    </div>
                    <div className="flex items-center">
                        <input type="checkbox" id="is_active" name="is_active" checked={formData.is_active === 1} onChange={handleChange} className="h-4 w-4" />
                        <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">Active (visible on the landing page)</label>
                    </div>
                    {error && <div className="text-red-500 text-sm">{error}</div>}
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="primary-gradient text-white py-2 px-4 rounded-lg disabled:opacity-50">
                            {isSubmitting ? 'Saving...' : 'Save Block'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdBlocksPage() {
    const [blocks, setBlocks] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);
    const [blockToEdit, setBlockToEdit] = React.useState(null);
    const [actionMessage, setActionMessage] = React.useState(null);
    const [analytics, setAnalytics] = React.useState(null);
    const [activeTab, setActiveTab] = React.useState('blocks');
    const [analyticsLoading, setAnalyticsLoading] = React.useState(false);
    const [history, setHistory] = React.useState([]);
    const [historyLoading, setHistoryLoading] = React.useState(false);

    const fetchBlocks = React.useCallback(() => {
        setLoading(true);
        fetch('/api/get_ad_blocks.php?scope=all', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setBlocks(data))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const fetchAnalytics = React.useCallback(() => {
        setAnalyticsLoading(true);
        fetch('/api/get_ad_block_analytics.php', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setAnalytics(data))
            .catch(err => console.error('Failed to fetch analytics:', err))
            .finally(() => setAnalyticsLoading(false));
    }, []);

    const fetchHistory = React.useCallback(() => {
        setHistoryLoading(true);
        fetch('/api/get_ad_block_history.php', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setHistory(data.history || []))
            .catch(err => console.error('Failed to fetch history:', err))
            .finally(() => setHistoryLoading(false));
    }, []);

    React.useEffect(() => { fetchBlocks(); }, [fetchBlocks]);
    React.useEffect(() => {
        if (activeTab === 'analytics') fetchAnalytics();
        if (activeTab === 'history') fetchHistory();
    }, [activeTab, fetchAnalytics, fetchHistory]);

    const handleAddClick = () => {
        setBlockToEdit(null);
        setShowModal(true);
    };

    const handleEditClick = (block) => {
        setBlockToEdit(block);
        setShowModal(true);
    };

    const handleDeleteClick = async (blockId) => {
        if (!window.confirm("Are you sure you want to delete this ad block? This cannot be undone.")) return;
        setActionMessage(null);
        const formData = new FormData();
        formData.append('action', 'delete');
        formData.append('id', blockId);
        try {
            const response = await fetch('/api/manage_ad_block.php', { method: 'POST', credentials: 'include', body: formData });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setActionMessage({ type: 'success', text: result.message });
            fetchBlocks();
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        }
    };

    return (
        <div data-name="ad-blocks-page" data-file="pages/AdBlocksPage.js" className="min-h-screen py-8">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-4xl font-bold text-white">Ad Blocks</h1>
                    <button onClick={handleAddClick} className="primary-gradient text-white font-bold py-2 px-4 rounded-lg hover:opacity-90">
                        <i className="fas fa-plus mr-2"></i>Add Block
                    </button>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            {[
                                { id: 'blocks', label: 'Ad Blocks', icon: 'fa-th-large' },
                                { id: 'analytics', label: 'Analytics', icon: 'fa-chart-bar' },
                                { id: 'history', label: 'History', icon: 'fa-history' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-6 text-sm font-medium whitespace-nowrap border-b-2 ${
                                        activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    <i className={`fas ${tab.icon} mr-2`}></i>
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {actionMessage && <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{actionMessage.text}</div>}
                {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>}
                {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}

                {!loading && !error && (
                    <>
                        {activeTab === 'blocks' && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <p className="text-sm text-gray-500 mb-4">These 4 blocks are displayed on the app landing page under the Buy / Sell buttons. Use the position to control which slot (1-4) each block occupies.</p>
                                {blocks.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No ad blocks yet. Click "Add Block" to create one.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Media</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {blocks.map(b => (
                                                    <tr key={b.id}>
                                                        <td className="px-6 py-4 font-medium text-gray-900">{b.position}</td>
                                                        <td className="px-6 py-4">
                                                            {b.product_data ? (() => {
                                                                try {
                                                                    const p = typeof b.product_data === 'string' ? JSON.parse(b.product_data) : b.product_data;
                                                                    return <img src={p.image_url_1 || p.image_url_2 || p.image_url_3 || p.image_url_4 || 'images/products/Wooden Box.png'} alt={p.name} className="h-12 w-auto rounded" onError={(e) => { e.target.src = 'images/products/Wooden Box.png'; }} />;
                                                                } catch (e) {
                                                                    return <span className="text-gray-400 text-xs">No image</span>;
                                                                }
                                                            })() : <span className="text-gray-400 text-xs">No product</span>}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {b.media && b.media.length > 0 ? (
                                                                <span className="text-blue-600 text-sm font-medium">{b.media.length} file(s)</span>
                                                            ) : (
                                                                <span className="text-gray-400 text-xs">No media</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 max-w-xs truncate">
                                                            {b.product_data ? (() => {
                                                                try {
                                                                    const p = typeof b.product_data === 'string' ? JSON.parse(b.product_data) : b.product_data;
                                                                    return <span className="text-green-700 text-sm font-medium truncate block">{p.name}</span>;
                                                                } catch (e) {
                                                                    return <span className="text-gray-400 text-xs">—</span>;
                                                                }
                                                            })() : <span className="text-gray-400 text-xs">—</span>}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${b.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                {b.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right space-x-2">
                                                            <button onClick={() => handleEditClick(b)} className="text-blue-600 hover:text-blue-900">Edit</button>
                                                            <button onClick={() => handleDeleteClick(b.id)} className="text-red-600 hover:text-red-900">Delete</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'analytics' && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Ad Block Analytics</h3>
                                {analyticsLoading ? (
                                    <div className="text-center py-8"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>
                                ) : analytics ? (
                                    <>
                                        {analytics.totals && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                                <div className="bg-blue-50 rounded-xl p-4">
                                                    <p className="text-sm text-gray-500">Total Views</p>
                                                    <p className="text-2xl font-bold text-gray-900">{analytics.totals.total_views || 0}</p>
                                                </div>
                                                <div className="bg-green-50 rounded-xl p-4">
                                                    <p className="text-sm text-gray-500">Total Clicks</p>
                                                    <p className="text-2xl font-bold text-gray-900">{analytics.totals.total_clicks || 0}</p>
                                                </div>
                                                <div className="bg-purple-50 rounded-xl p-4">
                                                    <p className="text-sm text-gray-500">Click-Through Rate</p>
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {analytics.totals.total_views > 0 ? ((analytics.totals.total_clicks / analytics.totals.total_views) * 100).toFixed(2) : 0}%
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {analytics.blocks && analytics.blocks.length > 0 && (
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {analytics.blocks.map(block => (
                                                            <tr key={block.id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{block.position}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{block.title}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${block.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                        {block.is_active ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{block.total_views || 0}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{block.total_clicks || 0}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-gray-500 text-center py-8">No analytics data available yet.</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'history' && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Ad Block History</h3>
                                {historyLoading ? (
                                    <div className="text-center py-8"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>
                                ) : history.length > 0 ? (
                                    <div className="space-y-4">
                                        {history.map(item => (
                                            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                                    <span className="text-sm text-gray-500">{new Date(item.created_at).toLocaleString()}</span>
                                                    <span className="text-sm font-medium text-gray-900">{item.ad_block_title || 'Deleted Block'}</span>
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        item.action === 'created' ? 'bg-green-100 text-green-800' :
                                                        item.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                                        item.action === 'deleted' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {item.action}
                                                    </span>
                                                    <span className="text-sm text-gray-500">by {item.changed_by_name || 'System'}</span>
                                                </div>
                                                
                                                {(item.new_values || item.old_values) && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {item.old_values && (
                                                            <div className="bg-gray-50 rounded-lg p-3">
                                                                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Previous Content</p>
                                                                <div className="space-y-1 text-sm">
                                                                    {item.old_values.title && <p><span className="font-medium">Title:</span> {item.old_values.title}</p>}
                                                                    {item.old_values.subtitle && <p><span className="font-medium">Subtitle:</span> {item.old_values.subtitle}</p>}
                                                                    {item.old_values.link && <p><span className="font-medium">Link:</span> {item.old_values.link}</p>}
                                                                    {item.old_values.position && <p><span className="font-medium">Position:</span> {item.old_values.position}</p>}
                                                                    {item.old_values.product_id && <p><span className="font-medium">Product ID:</span> {item.old_values.product_id}</p>}
                                                                    {item.old_values.image && <p><span className="font-medium">Image:</span> {item.old_values.image}</p>}
                                                                    {item.old_values.media_files && item.old_values.media_files.length > 0 && (
                                                                        <div>
                                                                            <span className="font-medium">Media Files:</span>
                                                                            <ul className="list-disc list-inside ml-2">
                                                                                {item.old_values.media_files.map((media, idx) => (
                                                                                    <li key={idx}>{media.file_name} ({media.file_type})</li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.new_values && (
                                                            <div className="bg-blue-50 rounded-lg p-3">
                                                                <p className="text-xs font-medium text-blue-500 uppercase mb-2">New Content</p>
                                                                <div className="space-y-1 text-sm">
                                                                    {item.new_values.title && <p><span className="font-medium">Title:</span> {item.new_values.title}</p>}
                                                                    {item.new_values.subtitle && <p><span className="font-medium">Subtitle:</span> {item.new_values.subtitle}</p>}
                                                                    {item.new_values.link && <p><span className="font-medium">Link:</span> {item.new_values.link}</p>}
                                                                    {item.new_values.position && <p><span className="font-medium">Position:</span> {item.new_values.position}</p>}
                                                                    {item.new_values.product_id && <p><span className="font-medium">Product ID:</span> {item.new_values.product_id}</p>}
                                                                    {item.new_values.image && <p><span className="font-medium">Image:</span> {item.new_values.image}</p>}
                                                                    {item.new_values.media_files && item.new_values.media_files.length > 0 && (
                                                                        <div>
                                                                            <span className="font-medium">Media Files:</span>
                                                                            <ul className="list-disc list-inside ml-2">
                                                                                {item.new_values.media_files.map((media, idx) => (
                                                                                    <li key={idx}>{media.file_name} ({media.file_type})</li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-8">No history available yet.</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {showModal && <AdBlockManagerModal isOpen={showModal} onClose={() => setShowModal(false)} block={blockToEdit} onSave={fetchBlocks} />}
        </div>
    );
}
